import { apiFetch } from '$lib/api/helpers';
import { API_BASE, getApiAuthHeaders } from '$lib/constants/api';
import type {
	LEDScene,
	LEDSceneWrite,
	LEDLoad,
	LEDMappingPreviewItem,
	LEDMappingRegenerateResponse,
	LEDMappingExportItem
} from '$lib/types/led_scene';

export class LEDMappingRegenerateConflict extends Error {
	manual_count: number;
	requires_confirmation: boolean;

	constructor(body: {
		detail: string;
		manual_count: number;
		requires_confirmation: boolean;
	}) {
		super(body.detail);
		this.name = 'LEDMappingRegenerateConflict';
		this.manual_count = body.manual_count;
		this.requires_confirmation = body.requires_confirmation;
	}
}

const SCENES_PATH = '/led-scenes/';
const LOADS_PATH = '/led-loads/';

export async function fetchLEDScenes(unitId: number): Promise<LEDScene[]> {
	return apiFetch<Array<LEDScene>>(`${SCENES_PATH}?unit_id=${unitId}`);
}

export async function fetchLEDScene(id: number): Promise<LEDScene> {
	return apiFetch<LEDScene>(`${SCENES_PATH}${id}/`);
}

export async function createLEDScene(data: LEDSceneWrite): Promise<LEDScene> {
	return apiFetch<LEDScene>(SCENES_PATH, {
		method: 'POST',
		body: JSON.stringify(data)
	});
}

export async function updateLEDScene(id: number, data: LEDSceneWrite): Promise<LEDScene> {
	return apiFetch<LEDScene>(`${SCENES_PATH}${id}/`, {
		method: 'PATCH',
		body: JSON.stringify(data)
	});
}

export async function deleteLEDScene(id: number): Promise<void> {
	return apiFetch<void>(`${SCENES_PATH}${id}/`, { method: 'DELETE' });
}

export async function deleteAllLEDScenes(unitId: number): Promise<void> {
	return apiFetch<void>(`/units/${unitId}/delete-all-led-scenes/`, { method: 'POST' });
}

export async function fetchLEDLoads(ledSceneId: number): Promise<LEDLoad[]> {
	return apiFetch<Array<LEDLoad>>(`${LOADS_PATH}?led_scene_id=${ledSceneId}`);
}

export async function createLEDLoad(data: Partial<LEDLoad>): Promise<LEDLoad> {
	return apiFetch<LEDLoad>(LOADS_PATH, {
		method: 'POST',
		body: JSON.stringify(data)
	});
}

export async function deleteLEDLoad(id: number): Promise<void> {
	return apiFetch<void>(`${LOADS_PATH}${id}/`, { method: 'DELETE' });
}

export async function previewLEDMappings(unitId: number): Promise<LEDMappingPreviewItem[]> {
	return apiFetch<Array<LEDMappingPreviewItem>>(`/units/${unitId}/led-mapping-preview/`);
}

export async function regenerateLEDMappings(
	unitId: number,
	force = false
): Promise<LEDMappingRegenerateResponse> {
	const response = await fetch(
		`${API_BASE}/units/${unitId}/led-mapping-regenerate/?force=${force}`,
		{
			method: 'POST',
			headers: getApiAuthHeaders()
		}
	);
	const body = (await response.json().catch(() => ({}))) as LEDMappingRegenerateResponse & {
		detail?: string;
		manual_count?: number;
		requires_confirmation?: boolean;
	};
	if (response.ok) {
		return body;
	}
	if (
		response.status === 409 &&
		body.requires_confirmation &&
		typeof body.manual_count === 'number'
	) {
		throw new LEDMappingRegenerateConflict({
			detail: body.detail ?? 'Manual edits require confirmation',
			manual_count: body.manual_count,
			requires_confirmation: true
		});
	}
	throw new Error(body.detail ?? `Unable to regenerate LED mappings (${response.status})`);
}

export async function exportLEDMappings(unitId: number): Promise<LEDMappingExportItem[]> {
	return apiFetch<Array<LEDMappingExportItem>>(`/units/${unitId}/led-mapping-export/`);
}
